import clsx from 'clsx';
import { components } from '@mosey/api-types';
import { Fieldset } from './Fieldset';
import { TaskDateField } from '../../common/forms/DateField';
import { TaskNumberField } from '../../common/forms/NumberField';
import { InlineCheckOrRadio } from './InlineCheckOrRadio';
import { TaskRadioField } from '../../common/forms/RadioField';
import {
  useAnswers,
  useIsBulkQuestion,
  useQuestionFieldVariant,
  useTaskActionData,
} from '../../utils/hooks';

type RadioParams = components['schemas']['RadioParams'];
type CheckboxParams = { options: components['schemas']['Option'][] };
type NumberParams = components['schemas']['NumberParams'];
type Field = components['schemas']['Field'];

interface TaskQuestionFieldProps {
  field: components['schemas']['Field'];
}

export const TaskQuestionField = ({ field }: TaskQuestionFieldProps) => {
  const required = Boolean(field.required);
  const isBulkQuestion = useIsBulkQuestion();
  const answers = useAnswers();
  const variant = useQuestionFieldVariant();
  const actionData = useTaskActionData();

  switch (field.component.type) {
    case 'fieldset':
      return (
        <Fieldset legend={field.label}>
          <div className="flex flex-col gap-y-3">
            {(field.component.params as { contents: Field[] }).contents.map(
              (fieldsetField) => (
                <TaskQuestionField
                  key={fieldsetField.name}
                  field={fieldsetField}
                />
              ),
            )}
          </div>
        </Fieldset>
      );

    case 'checkbox':
      return (
        // TODO: Error
        <Fieldset legend={field.label}>
          <div className="grid gap-x-4 gap-y-2 @md:grid-cols-2 @xl:grid-cols-3">
            {(field.component.params as CheckboxParams).options.map(
              (option) => {
                return (
                  <InlineCheckOrRadio
                    key={option.value}
                    type="checkbox"
                    label={option.name}
                    name={field.name}
                    value={option.value}
                    defaultChecked={answers[option.value] === 'true'}
                  />
                );
              },
            )}
          </div>
        </Fieldset>
      );

    case 'radio':
      if (variant === 'large') {
        return (
          <TaskRadioField
            value={answers[field.name] ?? undefined}
            field={field}
            errors={actionData?.errors}
          />
        );
      }

      return (
        // TODO: Error
        <Fieldset legend={field.label}>
          <div className="grid gap-x-4 gap-y-2 @md:grid-cols-2 @xl:grid-cols-3">
            {(field.component.params as RadioParams).options.map((option) => {
              return (
                <InlineCheckOrRadio
                  key={option.value}
                  type="radio"
                  label={option.name}
                  name={field.name}
                  value={option.value}
                  required={required}
                  defaultChecked={
                    isBulkQuestion
                      ? answers[option.value] === 'true'
                      : answers[field.name] === option.value
                  }
                />
              );
            })}
          </div>
        </Fieldset>
      );

    case 'number': {
      const { min, max, step } = (field.component.params as NumberParams) ?? {};

      return (
        <div className={clsx('w-full', variant === 'medium' && 'max-w-96')}>
          <TaskNumberField
            variant={variant}
            error={actionData?.errors?.[field.name]}
            label={field.label}
            name={field.name}
            defaultValue={answers[field.name] ?? undefined}
            min={min ?? undefined}
            max={max ?? undefined}
            step={step ?? undefined}
            required={required}
          />
        </div>
      );
    }

    case 'date':
      return (
        <TaskDateField
          variant={variant}
          error={actionData?.errors?.[field.name]}
          label={field.label}
          name={field.name}
          defaultValue={answers[field.name] ?? undefined}
          required={required}
        />
      );
  }

  return null;
};
